// Revise run — the non-question screens. Pure presentation over the contract.

import type {
  CarouselScreen,
  ContentScreen,
  FrameScreen,
  MtaIntroScreen,
  PlanScreen,
  RevealScreen,
  ReviseRun,
  ReviseSource,
  RubricScreen,
  RunsumScreen,
  SourceScreen,
  StatScreen,
  StoryScreen,
} from '../../../../../lib/revise/types';
import { useState } from 'react';
import DOMPurify from 'dompurify';
import { comparedSources, comparisonLabel } from '../../../../../lib/revise/paperJson';
import type { ReviseRunApi } from './useReviseRun';

/**
 * Every authored HTML string in a run is injected through this one helper, and
 * it sanitises. Runs arrive from the MCP and from the raw-JSON editor field, so
 * they are instructor-authored but not literals; one `onerror` attribute in a
 * source body would otherwise run for every learner. DOMPurify keeps the tags
 * the content uses (figure, svg, span.key, inline style on captions).
 */
export const html = (s: string | undefined) => ({ dangerouslySetInnerHTML: { __html: s ? DOMPurify.sanitize(s) : '' } });

export function Chip({ text, kind }: { text: string; kind?: 'accent' | 'instinct' }) {
  return <span className={`rr-chip${kind ? ` rr-chip-${kind}` : ''}`}>{text}</span>;
}

export function Story({ s }: { s: StoryScreen }) {
  return (
    <div className="rr-story">
      {(s.marker || s.markerLabel) && (
        <div className="rr-marker">
          {s.marker}
          {s.markerLabel && <small>{s.markerLabel}</small>}
        </div>
      )}
      <div className="rr-lines">
        {s.lines.map((l, i) => (
          <p key={i}>{l}</p>
        ))}
      </div>
    </div>
  );
}

export function Content({ s }: { s: ContentScreen }) {
  return (
    <>
      {s.tag && <Chip text={s.tag} />}
      <div className="rr-rich" {...html(s.html)} />
      {s.remember && <div className="rr-remember">{s.remember}</div>}
      {s.terms && s.terms.length > 0 && (
        <details className="rr-terms">
          <summary>Key terms</summary>
          <dl>
            {s.terms.map((t) => (
              <div key={t.term}>
                <dt>{t.term}</dt>
                <dd>{t.def}</dd>
              </div>
            ))}
          </dl>
        </details>
      )}
    </>
  );
}

export function Stat({ s }: { s: StatScreen }) {
  return (
    <>
      {s.tag && <Chip text={s.tag} />}
      <h2>{s.heading}</h2>
      {s.lead && <div className="rr-rich" {...html(s.lead)} />}
      <div className="rr-stats">
        {s.stats.map((st, i) => (
          <div key={i} className="rr-stat">
            <b>{st.num}</b>
            <span>{st.label}</span>
          </div>
        ))}
      </div>
    </>
  );
}

export function SourceCard({ source, run, compact }: { source: ReviseSource; run: ReviseRun; compact?: boolean }) {
  const compared = comparedSources(source, run.sources ?? []);
  if (compared.length > 0) return <ComparisonPanel compared={compared} run={run} compact={compact} />;
  return <SourceBody source={source} run={run} compact={compact} />;
}

function SourceBody({ source, run, compact }: { source: ReviseSource; run: ReviseRun; compact?: boolean }) {
  const image = source.imageId ? run.images?.find((i) => i.id === source.imageId) : undefined;
  return (
    <div className="rr-source">
      <div className="rr-source-head">
        <b>{source.label}</b>
        {source.type && <span>{source.type}</span>}
      </div>
      <div className="rr-source-body">
        {image && <img src={image.url} alt={image.alt} width={image.width} height={image.height} loading="lazy" />}
        {source.body && <div className="rr-rich" {...html(source.body)} />}
      </div>
      {source.summaryNotice && <div className="rr-source-notice">This is Kula's summary of the source; the original is in copyright.</div>}
      <div className="rr-source-attr">{source.attribution}</div>
      {source.toggle && !compact && (
        <details className="rr-source-toggle">
          <summary>A note on this source</summary>
          <div>{source.toggle}</div>
        </details>
      )}
    </div>
  );
}

/**
 * A comparison panel stands in for the sources it compares: one tab per
 * source, opening on the first, each in full (text and image). The panel's own
 * extracts are not shown; the learner reads the real sources instead.
 */
function ComparisonPanel({ compared, run, compact }: { compared: ReviseSource[]; run: ReviseRun; compact?: boolean }) {
  const [view, setView] = useState(0);
  const current = compared[view] ?? compared[0];
  return (
    <div className="rr-compare-panel">
      <div className="rr-source-tabs" role="tablist" aria-label="Sources">
        {compared.map((t, i) => (
          <button key={t.key} type="button" role="tab" aria-selected={i === view} className={i === view ? 'rr-sel' : ''} onClick={() => setView(i)}>
            {t.label}
          </button>
        ))}
      </div>
      <div role="tabpanel" aria-label={current.label}>
        <SourceBody source={current} run={run} compact={compact} />
      </div>
    </div>
  );
}

export function Source({ s, run }: { s: SourceScreen; run: ReviseRun }) {
  const source = run.sources?.find((x) => x.key === s.sourceKey);
  // Papers imported before comparisonLabel carry the importer's default tag,
  // "Source compare". Show the sources it compares instead; an authored tag
  // is left alone.
  const compared = source ? comparedSources(source, run.sources ?? []) : [];
  const tag = compared.length && s.tag === `Source ${s.sourceKey}` ? comparisonLabel(compared) : s.tag;
  return (
    <>
      {tag && <Chip text={tag} />}
      {s.html && <div className="rr-rich" {...html(s.html)} />}
      {source ? <SourceCard source={source} run={run} /> : <div className="rr-note">Source “{s.sourceKey}” is missing from this run.</div>}
    </>
  );
}

export function Carousel({ s }: { s: CarouselScreen }) {
  return (
    <>
      {s.tag && <Chip text={s.tag} />}
      <h2>{s.heading}</h2>
      <div className="rr-carousel" role="list">
        {s.cards.map((c, i) => (
          <div key={i} className="rr-card" role="listitem">
            <b>{c.lead}</b>
            <div className="rr-rich" {...html(c.body)} />
          </div>
        ))}
      </div>
    </>
  );
}

/** The running summary: every `part` of a build question the learner has chosen so far. */
export function Runsum({ s, run, api }: { s: RunsumScreen; run: ReviseRun; api: ReviseRunApi }) {
  const parts = run.screens
    .filter((x): x is Extract<typeof x, { type: 'question' }> => x.type === 'question' && x.mode === 'build' && !!x.part)
    .filter((q) => api.answers[q.id])
    .map((q) => q.part as string);
  return (
    <>
      {s.tag && <Chip text={s.tag} />}
      <h2>{s.heading}</h2>
      {s.label && <p className="rr-note">{s.label}</p>}
      <div className="rr-builder-para">
        {parts.length ? parts.map((p, i) => <p key={i}>{p}</p>) : <span className="rr-placeholder">Nothing yet. The build questions add to this as you go.</span>}
      </div>
    </>
  );
}

export function Reveal({ s, api }: { s: RevealScreen; api: ReviseRunApi }) {
  const shown = api.revealed[s.id] ?? 1;
  const all = shown >= s.items.length;
  return (
    <>
      {s.eyebrow && <span className="rr-eyebrow">{s.eyebrow}</span>}
      <h2>{s.heading}</h2>
      {s.lead && <p>{s.lead}</p>}
      <div className="rr-reveal-items">
        {s.items.slice(0, shown).map((it, i) => (
          <div key={i} className="rr-reveal-item">
            <b>
              {i + 1}. {it.lead}
            </b>
            {it.body && <div className="rr-rich" {...html(it.body)} />}
          </div>
        ))}
      </div>
      {!all && (
        <button type="button" onClick={() => api.revealNext(s.id, s.items.length)}>
          Next point
        </button>
      )}
      {all && s.close && <p className="rr-note">{s.close}</p>}
    </>
  );
}

export function Rubric({ s }: { s: RubricScreen }) {
  const bandLabels = s.dimensions[0]?.bands.map((b) => b.label) ?? [];
  return (
    <>
      {s.heading && <h2>{s.heading}</h2>}
      {s.intro && <p>{s.intro}</p>}
      <div className="rr-table-wrap">
        <table className="rr-table">
          <thead>
            <tr>
              <th>Dimension</th>
              {bandLabels.map((b) => (
                <th key={b}>{b}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {s.dimensions.map((d) => (
              <tr key={d.name}>
                <th>{d.name}</th>
                {d.bands.map((b, i) => (
                  <td key={i}>{b.text}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

export function Plan({ s }: { s: PlanScreen }) {
  return (
    <>
      <h2>{s.heading}</h2>
      {s.intro && <p>{s.intro}</p>}
      <div className="rr-table-wrap">
        <table className="rr-table">
          <thead>
            <tr>
              <th>Source</th>
              <th>For the stand</th>
              <th>Against it</th>
            </tr>
          </thead>
          <tbody>
            {s.rows.map((r) => (
              <tr key={r.k}>
                <th>{r.k}</th>
                <td {...html(r.yes)} />
                <td {...html(r.no)} />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

export function MtaIntro({ s }: { s: MtaIntroScreen }) {
  return (
    <>
      <Chip text="Mark this answer" kind="accent" />
      <h2>{s.heading}</h2>
      <div className="rr-rich" {...html(s.html)} />
    </>
  );
}

/** The skeleton's closing frame: every saved line, in writing order. */
export function Frame({ s, run, api }: { s: FrameScreen; run: ReviseRun; api: ReviseRunApi }) {
  const lines: Array<{ label: string; text: string }> = [];
  for (const x of run.screens) {
    if (x.type === 'pick' && api.picks[x.id] !== undefined) lines.push({ label: x.save, text: x.options[api.picks[x.id]] });
    if (x.type === 'evidence' && api.evidence[x.id]?.checked) {
      const chosen = api.evidence[x.id].chosen.map((i) => x.items[i]?.text).filter(Boolean);
      lines.push({ label: x.save, text: [x.claim, ...chosen].join(' · ') });
    }
  }
  return (
    <>
      <Chip text="Your skeleton" kind="accent" />
      {s.heading && <h2>{s.heading}</h2>}
      {s.html && <div className="rr-rich" {...html(s.html)} />}
      <div className="rr-plan">
        {lines.length === 0 && <div className="rr-note">Nothing saved yet.</div>}
        {lines.map((l, i) => (
          <div key={i} className="rr-plan-line">
            <b>{l.label}</b>
            {l.text}
          </div>
        ))}
      </div>
    </>
  );
}
